/**
 * AccessDeniedPage Component
 *
 * Displayed when a user attempts to access a resource they don't have permission for.
 */

import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShieldX, ArrowLeft, Home, HelpCircle, LogOut, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// Reasons that mean "we could not VERIFY access" -- a TRANSIENT, retryable
// authorization failure -- rather than "you lack permission". Access still fails
// closed, but the message must not tell a legitimately-authorized user (e.g. a
// super_admin) they were denied. These are the exact strings the permission
// service emits for the two retryable cases:
//   - 'Authorization check timed out' : the 6s client deadline won
//     (unifiedPermissionService.checkRouteAccessWithTimeout)
//   - 'Authorization check failed'     : the check threw / transport failure
// A genuinely missing profile ('User not found'), an unapproved user, or any
// real policy denial is NOT here: those stay the denial variant on purpose.
const VERIFICATION_FAILURE_REASONS = new Set(['Authorization check timed out', 'Authorization check failed']);

export interface AccessDeniedPageProps {
  /**
   * Reason for access denial
   */
  reason?: string;

  /**
   * Custom title
   */
  title?: string;

  /**
   * Custom description
   */
  description?: string;

  /**
   * Whether to show the go back button
   */
  showBackButton?: boolean;

  /**
   * Whether to show contact support link
   */
  showSupportLink?: boolean;

  /**
   * Render the "could not verify access, temporarily unavailable" variant
   * instead of a genuine permission denial. Inferred from `reason` when unset.
   */
  variant?: 'denied' | 'unavailable';

  /**
   * Retry handler for the unavailable variant. Falls back to a full reload.
   */
  onRetry?: () => void;
}

export const AccessDeniedPage: React.FC<AccessDeniedPageProps> = ({
  reason,
  title,
  description,
  showBackButton = true,
  showSupportLink = true,
  variant,
  onRetry,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, user } = useAuth();

  const unavailable = variant === 'unavailable'
    || (variant === undefined && reason !== undefined && VERIFICATION_FAILURE_REASONS.has(reason));
  const effectiveTitle = title ?? (unavailable ? 'Access check temporarily unavailable' : 'Access Denied');
  const effectiveDescription = description ?? (unavailable
    ? "We couldn't verify your access to this page right now. This is usually temporary — please retry."
    : "You don't have permission to access this page.");

  const handleRetry = () => {
    if (onRetry) onRetry();
    else window.location.reload();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6 ${unavailable ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
          {unavailable
            ? <RefreshCw className="w-10 h-10 text-amber-600 dark:text-amber-400" />
            : <ShieldX className="w-10 h-10 text-red-600 dark:text-red-400" />}
        </div>

        {/* Title */}
        <h1 data-testid={unavailable ? 'access-check-unavailable' : 'access-denied'} className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{effectiveTitle}</h1>

        {/* Description */}
        <p className="text-gray-600 dark:text-gray-400 mb-4">{effectiveDescription}</p>

        {/* Retry (verification failure only) */}
        {unavailable && (
          <div className="mb-6">
            <button
              onClick={handleRetry}
              data-testid="access-check-retry"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        )}

        {/* Reason */}
        {reason && (
          <div className={`border rounded-lg p-4 mb-6 ${unavailable ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
            <p className={`text-sm ${unavailable ? 'text-amber-700 dark:text-amber-300' : 'text-red-700 dark:text-red-300'}`}>
              <span className="font-medium">{unavailable ? 'Detail:' : 'Reason:'}</span> {reason}
            </p>
          </div>
        )}

        {/* User Info */}
        {user && (
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Logged in as: <span className="font-medium text-gray-900 dark:text-white">{user.email}</span>
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {showBackButton && (
            <button
              onClick={handleGoBack}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
          )}

          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Home className="w-4 h-4" />
            Go to Home
          </Link>
        </div>

        {/* Secondary Actions */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-3 justify-center text-sm">
          {showSupportLink && (
            <a
              href="mailto:support@propellertravel.com"
              className="inline-flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              Contact Support
            </a>
          )}

          <button
            onClick={handleSignOut}
            className="inline-flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>

        {/* Debug Info (only in development) */}
        {import.meta.env.DEV && (
          <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-left text-xs text-gray-500 dark:text-gray-400">
            <p className="font-medium mb-2">Debug Info:</p>
            <pre className="whitespace-pre-wrap break-all">
              {JSON.stringify(
                {
                  path: location.pathname,
                  reason,
                  userId: user?.id,
                  timestamp: new Date().toISOString(),
                },
                null,
                2
              )}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessDeniedPage;
